import { desktopEnvironment, readDesktopSeenAt, type DesktopStatus } from '@/services/desktopConnectionService';

/**
 * Desktop note handoff — the browser side of
 * `coordination/contracts/2026-09-09-desktop-note-handoff.md`.
 *
 * Owns the field names, the deep link, and the per-browser preference. The
 * loopback client itself stays in `desktopConnectionService`; this module never
 * opens a second one.
 */

/**
 * The capability token a desktop build advertises when it can accept a note
 * deep link. Exactly this spelling — lowercase, single hyphen.
 */
export const NOTE_HANDOFF_CAPABILITY = 'note-handoff';

/**
 * The capability token a desktop build advertises when it can accept a share
 * link — `notego-desktop://shared?shareToken=…`. Distinct from `note-handoff`
 * on purpose: no shipped desktop build (through 1.2.53) routes a `shared`
 * hostname — each drops it as an unexpected protocol URL — so the web must never
 * send one on the strength of `note-handoff` alone.
 */
export const SHARED_NOTE_HANDOFF_CAPABILITY = 'shared-note-handoff';

/** One of `ask`, `always`, `never`. */
export const HANDOFF_PREFERENCE_KEY = 'notego.desktopHandoff.preference';
/** JSON array of ids already handed off in this tab session: noteIds from `/note/:noteId`, share tokens from `/shared/:token`. */
export const HANDOFF_HANDED_KEY = 'notego.desktopHandoff.handed';
/** `?desktop=0` suppresses handoff for one navigation. No other value is meaningful. */
export const HANDOFF_SUPPRESS_PARAM = 'desktop';

export type HandoffPreference = 'ask' | 'always' | 'never';

const PREFERENCES: HandoffPreference[] = ['ask', 'always', 'never'];

/** How long a loopback sighting stands in for a live probe. */
const SIGHTING_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** One shape for both route params: a Firestore note id and a public share token. */
const HANDOFF_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

/** The route param is passed to the desktop app verbatim, so it is validated first. */
export function isHandoffNoteId(value: unknown): value is string {
  return typeof value === 'string' && HANDOFF_ID_PATTERN.test(value);
}

/** Same rule as `isHandoffNoteId`: the token goes into the deep link verbatim. */
export function isHandoffShareToken(value: unknown): value is string {
  return typeof value === 'string' && HANDOFF_ID_PATTERN.test(value);
}

/**
 * Whether this desktop build can accept a note deep link. The capability list is
 * the only signal — never the `version` string, which says nothing about what
 * the build can do and would strand every future release behind a comparison.
 */
export function supportsNoteHandoff(status: DesktopStatus | null): boolean {
  return !!status?.capabilities?.includes(NOTE_HANDOFF_CAPABILITY);
}

/** Whether this desktop build can accept a share-link deep link. Capability list only, as above. */
export function supportsSharedNoteHandoff(status: DesktopStatus | null): boolean {
  return !!status?.capabilities?.includes(SHARED_NOTE_HANDOFF_CAPABILITY);
}

export function readHandoffPreference(): HandoffPreference {
  try {
    const raw = localStorage.getItem(HANDOFF_PREFERENCE_KEY);
    return PREFERENCES.includes(raw as HandoffPreference) ? (raw as HandoffPreference) : 'ask';
  } catch {
    return 'ask';
  }
}

export function writeHandoffPreference(preference: HandoffPreference): void {
  try {
    localStorage.setItem(HANDOFF_PREFERENCE_KEY, preference);
  } catch {
    // Nothing to recover: the choice applies to this page view either way.
  }
}

/**
 * Whether this browser saw the desktop app recently enough to fire the deep link
 * without a live probe. This is the app-installed-but-not-running case, so it is
 * deliberately narrow: it stands in for evidence, never for consent, and the
 * caller must also hold an `always` preference before using it.
 */
export function hasRecentDesktopSighting(now = Date.now()): boolean {
  const seenAt = readDesktopSeenAt();
  return seenAt !== null && now - seenAt < SIGHTING_MAX_AGE_MS && seenAt <= now;
}

function readHandedNotes(): string[] {
  try {
    const raw = sessionStorage.getItem(HANDOFF_HANDED_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === 'string') : [];
  } catch {
    return [];
  }
}

/** True once this note has been handed off — or explicitly declined — in this tab. */
export function isNoteHanded(noteId: string): boolean {
  return readHandedNotes().includes(noteId);
}

export function markNoteHanded(noteId: string): void {
  try {
    const handed = readHandedNotes();
    if (handed.includes(noteId)) return;
    sessionStorage.setItem(HANDOFF_HANDED_KEY, JSON.stringify([...handed, noteId]));
  } catch {
    // Worst case the person sees the interstitial again on a later visit in
    // this tab. Storage failure must never block the note itself.
  }
}

/**
 * The deep link for a note, or null when the note id or the API origin is not
 * one this build can name. Carries no token, session, uid, or any other
 * credential — the desktop app resolves access through its own note query.
 */
export function desktopNoteUrl(noteId: string): string | null {
  if (!isHandoffNoteId(noteId)) return null;
  const environment = desktopEnvironment();
  if (!environment) return null;
  const query = new URLSearchParams({ noteId, environment });
  return `notego-desktop://note?${query}`;
}

/**
 * The deep link for a share token, or null when the token or the API origin is
 * not one this build can name. The `note` link cannot serve a share link: the
 * desktop resolves it through `GET /notes/{id}`, which is owner/invitee-gated,
 * so a public-link viewer would land on the desktop's permission state. This
 * link names the token instead, for the desktop's read-only `/shared/<token>`
 * view over the same public endpoint the browser uses. A share token is already
 * the note's public credential, so nothing else rides along. The query key is
 * `shareToken`, never `token`: the desktop's auth-callback handler decides it is
 * holding a Firebase credential by that key name alone, and a share token must
 * never be mistakable for one.
 */
export function desktopSharedNoteUrl(token: string): string | null {
  if (!isHandoffShareToken(token)) return null;
  const environment = desktopEnvironment();
  if (!environment) return null;
  const query = new URLSearchParams({ shareToken: token, environment });
  return `notego-desktop://shared?${query}`;
}

/** `?desktop=0` on the current URL opts this navigation out of handoff. */
export function isHandoffSuppressed(search: string): boolean {
  return new URLSearchParams(search).get(HANDOFF_SUPPRESS_PARAM) === '0';
}
